import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TIP_SHEET_TYPES } from './constants.jsx';
import { ChevronDownIcon } from '../TipSheetIcons/chevron-down.jsx';

const TipSheetTypeWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: ${(props) =>
    props.$variant === 'sidebar' ? 'flex-start' : 'center'};
  flex-direction: ${(props) =>
    props.$variant === 'sidebar' ? 'column' : 'row'};
  width: ${(props) =>
    props.$variant === 'sidebar' || props.$isMobile ? '100%' : 'auto'};

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const TipSheetTypeButton = styled.button`
  border-radius: 0.25rem;
  padding-left: 0.75rem;
  margin: 0;
  align-items: center;
  height: 2rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  cursor: pointer;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  color: #212121;
  font-size: 0.875rem;
  width: ${(props) => (props.$isMobile ? '100%' : '9.375rem')};
  justify-content: space-between;
  white-space: nowrap;
`;

const TipSheetTypeDropdown = styled.div`
  position: ${(props) =>
    props.$variant === 'sidebar'
      ? 'static'
      : props.$isMobile
        ? 'static'
        : 'absolute'};
  padding: ${(props) =>
    props.$variant === 'sidebar' ? '0' : props.$isMobile ? '0' : '0.75rem'};
  border-radius: ${(props) =>
    props.$variant === 'sidebar' ? '0' : props.$isMobile ? '0' : '0.25rem'};
  top: ${(props) => (props.$isMobile ? 'auto' : '2.5rem')};
  right: ${(props) => (props.$isMobile ? 'auto' : '0')};
  width: ${(props) =>
    props.$variant === 'sidebar'
      ? '100%'
      : props.$isMobile
        ? '100%'
        : '25rem'};
  background-color: ${(props) =>
    props.$variant === 'sidebar'
      ? 'transparent'
      : props.$isMobile
        ? 'transparent'
        : '#ffffff'};
  box-shadow: ${(props) =>
    props.$variant === 'sidebar'
      ? 'none'
      : props.$isMobile
        ? 'none'
        : '0px 14px 64px -20px #000d512b'};
  border: ${(props) =>
    props.$variant === 'sidebar'
      ? 'none'
      : props.$isMobile
        ? 'none'
        : '1px solid #cccccc'};

  @media (min-width: 769px) and (max-width: 1279px) {
    left: ${(props) => (props.$variant === 'sidebar' ? 'auto' : '0')};
  }
`;

const SidebarTypeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SidebarTypeTitle = styled.span`
  font-size: 0.9rem;
  color: #000000;
`;

const ShowDetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0a819f;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #0e4c60;
  }
`;

const TipSheetTypeBoxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  color: #212121;
`;

const TipSheetTypeItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TipSheetTypeDescription = styled.div`
  font-size: 0.75rem;
  padding-left: 50px; /* Align with the label text */
  color: #666666;
  margin-top: -0.25rem;
`;

const TypeToggleButton = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
  width: 100%;
  color: #212121;
  font-weight: 600;

  /* Hide the default checkbox but keep it focusable */
  & input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  /* Create the toggle switch appearance */
  & .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
    background-color: #cccccc;
    border-radius: 20px;
    margin-right: 10px;
    transition: all 0.3s;
  }

  /* Create the switch handle */
  & .switch:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    border-radius: 50%;
    background-color: #ffffff;
    transition: all 0.3s;
  }

  /* Styles for checked state */
  & input:checked + .switch {
    background-color: #0a819f;
  }

  & input:checked + .switch:before {
    transform: translateX(20px);
  }

  /* Focus styles for keyboard navigation */
  & input:focus + .switch {
    outline: 2px solid #0a819f;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px rgba(17, 135, 178, 0.3);
  }

  /* High contrast mode focus styles */
  @media (forced-colors: active) {
    & input:focus + .switch {
      outline: 2px solid CanvasText;
    }
  }

  /* Hover effects */
  &:hover .switch {
    box-shadow: 0 0 1px 1px rgba(0, 0, 0, 0.1);
  }
`;


const ChevronUpIcon = styled(ChevronDownIcon)`
  transform: rotate(180deg);
`;

export default function TipSheetTypeFilter({
  tipsheetType,
  setTipsheetType,
  selectedExclusions,
  handleExclusionsClick,
  hideInfo,
  isMobile = false,
  variant = 'dropdown',
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isMobile]);

  // The filter content that appears in the dropdown
  const FilterContent = () => (
    <>
      <TipSheetTypeBoxes>
        {TIP_SHEET_TYPES.map((types) => {
          const isSelected = tipsheetType.includes(
            types.name.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
          );

          return (
            <TipSheetTypeItem key={types.name}>
              <TypeToggleButton>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    setTipsheetType(
                      isSelected
                        ? tipsheetType.filter(
                            (type) =>
                              type !==
                              types.name
                                .replace(/[^a-zA-Z0-9]/g, '')
                                .toUpperCase()
                          )
                        : [
                            ...tipsheetType,
                            types.name
                              .replace(/[^a-zA-Z0-9]/g, '')
                              .toUpperCase(),
                          ]
                    );
                  }}
                />
                <span className="switch"></span>
                {types.name}
              </TypeToggleButton>
              {(variant !== 'sidebar' || showDetails) && (
                <TipSheetTypeDescription>
                  {types.description}
                </TipSheetTypeDescription>
              )}
            </TipSheetTypeItem>
          );
        })}

        {hideInfo?.length > 0 &&
          hideInfo.map((hideItem, i) => {
            const isSelected = !selectedExclusions.includes(hideItem.key);

            return (
              <TypeToggleButton key={i}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    handleExclusionsClick(hideItem.key);
                  }}
                />
                <span className="switch"></span>
                {hideItem.text}
              </TypeToggleButton>
            );
          })}
      </TipSheetTypeBoxes>
    </>
  );

  if (variant === 'sidebar') {
    return (
      <TipSheetTypeWrapper
        ref={dropdownRef}
        $isMobile={isMobile}
        $variant="sidebar"
      >
        <SidebarTypeHeader>
          <SidebarTypeTitle>Tip Sheet Types</SidebarTypeTitle>
          <ShowDetailsLink
            type="button"
            onClick={() => setShowDetails((v) => !v)}
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </ShowDetailsLink>
        </SidebarTypeHeader>
        <TipSheetTypeDropdown
          $isMobile={isMobile}
          $variant="sidebar"
        >
          <FilterContent />
        </TipSheetTypeDropdown>
      </TipSheetTypeWrapper>
    );
  }

  return (
    <TipSheetTypeWrapper ref={dropdownRef} $isMobile={isMobile}>
      {/* Only show the button in desktop mode */}
      {!isMobile && (
        <TipSheetTypeButton
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          $isMobile={isMobile}
        >
          Tip Sheet Types
          {isDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </TipSheetTypeButton>
      )}

      {/* In mobile mode, always show the content without a dropdown */}
      {/* In desktop mode, only show when dropdown is open */}
      {(isMobile || isDropdownOpen) && (
        <TipSheetTypeDropdown $isMobile={isMobile}>
          <FilterContent />
        </TipSheetTypeDropdown>
      )}
    </TipSheetTypeWrapper>
  );
}
